import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';

export interface AsyncSelectOption {
  value: string;
  label: string;
  sublabel?: string;
}

export interface AsyncSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  options: AsyncSelectOption[];
  isLoading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  minCharactersText?: string;
  minCharacters?: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedLabel?: string;
  disabled?: boolean;
  className?: string;
}

const AsyncSelect = React.forwardRef<HTMLButtonElement, AsyncSelectProps>(
  (
    {
      value,
      onValueChange,
      options,
      isLoading = false,
      placeholder = 'Select...',
      searchPlaceholder = 'Search...',
      noResultsText = 'No results found',
      minCharactersText = 'Type at least 3 characters to search',
      minCharacters = 3,
      searchValue,
      onSearchChange,
      selectedLabel,
      disabled = false,
      className,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);

    const handleSelect = (optionValue: string) => {
      onValueChange(optionValue);
      setOpen(false);
      onSearchChange('');
    };

    const showMinCharsMessage = searchValue.length > 0 && searchValue.length < minCharacters;
    const showNoResults = searchValue.length >= minCharacters && !isLoading && options.length === 0;
    const showOptions = searchValue.length >= minCharacters && options.length > 0;
    const showInitialState = searchValue.length === 0;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden',
              className
            )}
          >
            <span className={cn('truncate min-w-0', !value && 'text-muted-foreground')}>
              {selectedLabel || placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-9 border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          </div>
          <ScrollArea className="max-h-[200px]">
            <div className="p-1">
              {/* Initial state - no search yet */}
              {showInitialState && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {minCharactersText}
                </div>
              )}

              {/* Min characters message */}
              {showMinCharsMessage && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {minCharactersText}
                </div>
              )}

              {/* Loading state */}
              {isLoading && searchValue.length >= minCharacters && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </div>
              )}

              {/* No results */}
              {showNoResults && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {noResultsText}
                </div>
              )}

              {/* Options list */}
              {showOptions && !isLoading && (
                options.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                      value === option.value && 'bg-accent'
                    )}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="break-words">{option.label}</span>
                      {option.sublabel && (
                        <span className="text-xs text-muted-foreground break-words">{option.sublabel}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    );
  }
);

AsyncSelect.displayName = 'AsyncSelect';

export { AsyncSelect };
